import { useMemo } from "react"
import { chain, isEmpty, set, size } from "lodash"
import useResource from "hooks/resources"
import { generateClassificationQuery } from "helpers/classification"
import { combineTanStackStatuses } from "helpers/data-statuses"
import { IsoDate } from "helpers/types/dates"
import { Filter } from "connected-components/dashboard/context/types"
import { NonEmptyArray } from "ts-essentials"

interface ClassificationTier {
  id: string
  name: string
  level: number
}

export interface ClassificationVersion {
  id: string
  asOf: IsoDate
  tiers: NonEmptyArray<ClassificationTier>
}

export interface ClassificationMeta {
  id: string
  name: string
  proprietary: boolean
  versions: NonEmptyArray<ClassificationVersion>
}

interface ClassificationValue {
  id: string
  name: string
  values?: ClassificationValue[]
}

export interface Classification {
  version: {
    values: ClassificationValue[]
  }
}

const CLASSIFICATIONS_QUERY = `query {
  classifications {
    name
    id
    proprietary
    versions{
      id
      asOf
      tiers{
        id
        level
        name
      }
    }
  }
}`

export function useClassifications() {
  return useResource<ClassificationMeta[]>({
    resource: "classifications",
    url: () => ({
      id: "classifications",
      query: CLASSIFICATIONS_QUERY,
    }),
  }, "classifications")
}

interface FlatClassificationMeta {
  id: string
  versionId: string
  tiers: NonEmptyArray<ClassificationTier>
  levels: number
}

export function useClassificationsValues(classifications: ClassificationMeta[] | null, startDate: IsoDate) {
  const classificationsTiers = useMemo(() => {
    if (!classifications) return []
    return classifications.reduce((acc: FlatClassificationMeta[], classification: ClassificationMeta) => {
      const version = classification.versions[classification.versions.length - 1]
      return [
        ...acc,
        {
          tiers: version.tiers,
          id: classification.id,
          versionId: version.id,
          levels: version.tiers.length,
        },
      ]
    }, [])
  }, [classifications])

  return useResource<Record<string, Classification> | null>({
    // Slot keyed by date: a dashboard panel with its own period and the dashboard filter
    // selector read this resource for different dates, and one shared slot made them
    // overwrite each other's id until React hit its update limit (APP-3649).
    resource: `classificationsValues-${startDate}`,
    url: () => {
      if (size(classificationsTiers) === 0 || !startDate) {
        return null
      }

      return {
        // [TODO] I'm worried that the stringification might not be stable.
        // It seems to work in production, so maybe it's fine, but ideally we'd find something safer.
        id: `classificationsValues-${startDate}-${JSON.stringify(classificationsTiers)}`,
        query: `query {
          ${generateClassificationQuery(classificationsTiers)}
        }`,
      }
    },
  }, [])
}

interface FlattenedClassification extends ClassificationValue {
  value: string
  label: string
}

function recursiveGetClassificationFilter(filter: Filter | undefined, id: string, value: ClassificationValue, tier = 0): FlattenedClassification | FlattenedClassification[] {
  if (value.values) {
    return value.values.flatMap((v: ClassificationValue) => recursiveGetClassificationFilter(filter, id, v, tier + 1))
  }

  return {
    ...value,
    value: value.id,
    label: value.name,
  }
}

export function useClassificationsData(currentDate: IsoDate, filter: Filter | undefined) {
  const [classifications, classificationsStatus] = useClassifications()
  const [classificationsValues, classificationsValuesStatus] = useClassificationsValues(classifications, currentDate)

  const flattenedClassificationsValues = useMemo((): Record<string, { version: { values: FlattenedClassification[] } }> => {
    if (isEmpty(classificationsValues)) {
      return {}
    }
    return Object.entries(classificationsValues).reduce((acc, [id, classification]) => {
      if (!classification) {
        return acc
      }
      const flattenedValues = classification.version.values.flatMap(value => recursiveGetClassificationFilter(filter, id, value))
      return set(acc, [id, "version", "values"], flattenedValues)
    }, {})
  }, [classificationsValues, filter])

  return {
    classifications,
    classificationsValues,
    flattenedClassificationsValues,
    status: combineTanStackStatuses([classificationsStatus, classificationsValuesStatus]),
  }
}

// [TODO] This is eerily similar to the logic in recursiveGetClassificationFilter
export function flattenClassificationValues(tiers: ClassificationTier[], values: ClassificationValue[] | undefined, currentLevel = 0): {
  label: string
  level: number
  tierId: string
  value: string
}[] {
  return chain(values)
    .map(v => [
      {
        value: v.id,
        label: `${v.name} - ${v.id}`,
        level: currentLevel,
        tierId: chain(tiers)
          .find(t => t.level === currentLevel)
          .get("id")
          .value(),
      },
      ...(v.values ? flattenClassificationValues(tiers, v.values, currentLevel + 1) : []),
    ])
    .flatten()
    .value()
}
